import type { Observation } from "@medplum/fhirtypes";
import type { AnsweredValue, MemberId } from "@repo/domain";
import { required } from "../fhir/read";
import { unitFromQuantity, unitToQuantity } from "./ucum";

type ValueSlots = Pick<
  Observation,
  "valueString" | "valueQuantity" | "valueInteger" | "valueBoolean" | "valueCodeableConcept"
>;

/** `choiceSystem` codes a Choice value's selections: each caller's options live in its own system. */
export function valueToObservation(value: AnsweredValue, choiceSystem: string): ValueSlots {
  switch (value.kind) {
    case "Quantity":
      return { valueQuantity: { value: value.value, ...unitToQuantity(value.unit) } };
    case "Integer":
      return { valueInteger: value.value };
    case "Boolean":
      return { valueBoolean: value.value };
    case "Choice":
      return {
        valueCodeableConcept: {
          coding: value.selected.map((code) => ({ system: choiceSystem, code })),
          ...(value.freeText ? { text: value.freeText } : {}),
        },
      };
    case "Text":
      return { valueString: value.value };
  }
}

/**
 * The stored `value[x]` slot decides the kind, and a slot holding none of them
 * is a malformed row rather than empty text. Text is read last because it is
 * the only kind whose absence is indistinguishable from a blank answer, so it
 * is the one that has to be required rather than defaulted. A reading that
 * genuinely stored `valueString: ""` still round-trips as `Text ""`.
 */
export function valueFromObservation(source: ValueSlots, row: Observation, choiceSystem: string): AnsweredValue {
  if (source.valueQuantity?.value !== undefined) {
    return {
      kind: "Quantity",
      value: source.valueQuantity.value,
      ...(unitFromQuantity(source.valueQuantity) ? { unit: unitFromQuantity(source.valueQuantity) } : {}),
    };
  }
  if (source.valueInteger !== undefined) return { kind: "Integer", value: source.valueInteger };
  if (source.valueBoolean !== undefined) return { kind: "Boolean", value: source.valueBoolean };
  if (source.valueCodeableConcept) {
    return {
      kind: "Choice",
      selected: (source.valueCodeableConcept.coding ?? []).flatMap((coding) =>
        coding.system === choiceSystem && coding.code ? [coding.code as MemberId] : [],
      ),
      ...(source.valueCodeableConcept.text ? { freeText: source.valueCodeableConcept.text } : {}),
    };
  }
  return { kind: "Text", value: required(row, "components[].value", source.valueString) };
}
